/**
 * OnboardingModal — first-run profile questionnaire.
 *
 * Shown once per account (localStorage flag `omni:onboarding-v1`).
 * Answers are written to the memory module as user-scoped facts so
 * the AI can retrieve them for personalisation.
 *
 * Spec: Docs/design/04-shell-and-navigation.md §10
 */
import * as React from "react";
import { putMyProfile } from "@/api";
import { useApiClient } from "@/api/useApiClient";
import { apiErrorMessage } from "@/lib/api-error";
import { cn } from "@/lib/cn";

import { ONBOARDING_DONE_KEY } from "./onboarding";

interface ProfileDraft {
  name: string;
  role: string;
  uses: string[];
  language: string;
  bio: string;
}

const ROLES = [
  "Student",
  "Developer / Engineer",
  "Researcher",
  "Working Professional",
  "Creator / Designer",
  "Other",
];

const USE_CASES = [
  "Productivity & planning",
  "Coding & development",
  "Research & learning",
  "Creative work",
  "Conversations & advice",
  "Automations & tasks",
];

const LANGUAGES = [
  "English",
  "Hindi",
  "Spanish",
  "French",
  "German",
  "Portuguese",
  "Japanese",
  "Chinese (Mandarin)",
  "Arabic",
  "Other",
];

function buildProfilePayload(d: ProfileDraft) {
  return {
    name: d.name.trim() || null,
    role: d.role || null,
    uses: d.uses.length ? d.uses.join(", ") : null,
    language: d.language || null,
    bio: d.bio.trim() || null,
  };
}

export function OnboardingModal({ onDone }: { onDone: () => void }) {
  const client = useApiClient();
  // Every label is tied to its control: unassociated, a screen reader read
  // the inputs as unnamed (RW-1.24a, axe on first sign-in).
  const id = React.useId();
  const [draft, setDraft] = React.useState<ProfileDraft>({
    name: "",
    role: "",
    uses: [],
    language: "English",
    bio: "",
  });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const setField = <K extends keyof ProfileDraft>(
    key: K,
    val: ProfileDraft[K],
  ) => setDraft((d) => ({ ...d, [key]: val }));

  const toggleUse = (use: string) =>
    setDraft((d) => ({
      ...d,
      uses: d.uses.includes(use)
        ? d.uses.filter((u) => u !== use)
        : [...d.uses, use],
    }));

  const skip = () => {
    localStorage.setItem(ONBOARDING_DONE_KEY, "1");
    onDone();
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await putMyProfile({ client, body: buildProfilePayload(draft), throwOnError: true });
    } catch (e) {
      // Stay open: the answers are only in this form. This used to set the
      // error and then close anyway in `finally`, so the message was never
      // seen and the answers were gone.
      setError(`Couldn't save your profile: ${apiErrorMessage(e)}. Try again, or skip and fill it in later in Settings → Profile.`);
      setSaving(false);
      return;
    }
    setSaving(false);
    localStorage.setItem(ONBOARDING_DONE_KEY, "1");
    onDone();
  };

  return (
    <div
      className="om-fade-in fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "var(--om-color-overlay)" }}
    >
      <div className="om-fade-in-up w-full max-w-lg bg-bg-elevated border border-border">
        {/* Header */}
        <div className="border-b border-border px-6 py-5">
          <h2 className="text-base font-semibold text-fg">
            Tell Omni about yourself
          </h2>
          <p className="mt-1 text-xs text-fg-muted">
            Helps personalise your experience. All fields are optional — skip
            anything. You can edit these anytime in{" "}
            <strong>Settings → Profile</strong>.
          </p>
        </div>

        {/* Form */}
        <div className="px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
          {/* Preferred name */}
          <div>
            <label htmlFor={`${id}-name`} className="block text-xs font-medium text-fg mb-1.5">
              What should Omni call you?
            </label>
            <input
              id={`${id}-name`}
              type="text"
              value={draft.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="e.g. Alex or Omi"
              maxLength={40}
              className="w-full h-8 px-3 text-sm border border-border bg-bg text-fg placeholder:text-fg-faint focus:outline-none focus:border-border-strong"
            />
          </div>

          {/* Role */}
          <div>
            <p id={`${id}-role`} className="block text-xs font-medium text-fg mb-1.5">
              What best describes you?
            </p>
            <div role="group" aria-labelledby={`${id}-role`} className="flex flex-wrap gap-2">
              {ROLES.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setField("role", draft.role === r ? "" : r)}
                  className={cn(
                    "px-3 py-1 text-xs border transition-colors",
                    draft.role === r
                      ? "border-accent bg-accent-bg text-accent"
                      : "border-border text-fg-muted hover:border-border-strong hover:text-fg",
                  )}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          {/* Primary use cases */}
          <div>
            <p id={`${id}-uses`} className="block text-xs font-medium text-fg mb-1.5">
              What will you use Omni for most?{" "}
              <span className="text-fg-faint">(pick any)</span>
            </p>
            <div role="group" aria-labelledby={`${id}-uses`} className="flex flex-wrap gap-2">
              {USE_CASES.map((u) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => toggleUse(u)}
                  className={cn(
                    "px-3 py-1 text-xs border transition-colors",
                    draft.uses.includes(u)
                      ? "border-accent bg-accent-bg text-accent"
                      : "border-border text-fg-muted hover:border-border-strong hover:text-fg",
                  )}
                >
                  {draft.uses.includes(u) ? "✓ " : ""}
                  {u}
                </button>
              ))}
            </div>
          </div>

          {/* Language */}
          <div>
            <label htmlFor={`${id}-language`} className="block text-xs font-medium text-fg mb-1.5">
              Preferred language
            </label>
            <select
              id={`${id}-language`}
              value={draft.language}
              onChange={(e) => setField("language", e.target.value)}
              className="h-8 px-3 text-xs border border-border bg-bg text-fg focus:outline-none focus:border-border-strong"
            >
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </div>

          {/* Bio / interests */}
          <div>
            <label htmlFor={`${id}-bio`} className="block text-xs font-medium text-fg mb-1.5">
              Anything else Omni should know?{" "}
              <span className="text-fg-faint">(optional)</span>
            </label>
            <textarea
              id={`${id}-bio`}
              value={draft.bio}
              onChange={(e) => setField("bio", e.target.value)}
              placeholder="Interests, working hours, preferred response style…"
              rows={3}
              maxLength={300}
              className="w-full resize-none px-3 py-2 text-sm border border-border bg-bg text-fg placeholder:text-fg-faint focus:outline-none focus:border-border-strong"
            />
          </div>

          {error && (
            <p className="text-xs text-danger" role="alert">
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border px-6 py-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={skip}
            className="text-xs text-fg-faint hover:text-fg-muted transition-colors"
          >
            Skip for now
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving}
            className="h-8 px-5 text-xs bg-accent text-accent-fg hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {saving ? "Saving…" : "Save to Omni's memory"}
          </button>
        </div>
      </div>
    </div>
  );
}
